import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { StatusBadge } from '../components/UI';
import { api } from '../api';

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.getAnalytics().then(setData).catch(() => {}); }, []);

  const stats = data ? [
    { label: "Total leads",      value: data.totalLeads,    color: "#534AB7" },
    { label: "Appointments set", value: data.totalAppts,    color: "#1D9E75" },
    { label: "Messages sent",    value: data.totalMessages, color: "#378ADD" },
    { label: "Policies sold",    value: data.totalPolicies, color: "#D85A30" },
    { label: "Response rate",    value: data.responseRate ? data.responseRate + "%" : "—", color: "#BA7517" },
    { label: "Booking rate",     value: data.bookingRate  ? data.bookingRate  + "%" : "—", color: "#0F6E56" },
  ] : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 720 }}>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.2)", padding: "20px", boxShadow: "0 0 20px rgba(83,74,183,0.05)" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</div>
            <div style={{ fontSize: 36, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* By status */}
      {data && data.byStatus && (
        <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Leads by status</span>
          </div>
          <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
            {(Array.isArray(data.byStatus)
              ? data.byStatus.map(item => [item.status || item._id, item.count])
              : Object.entries(data.byStatus)
            ).map(([status, count]) => (
              <div key={status} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <StatusBadge status={status} />
                <div style={{ flex: 1, height: 8, background: "var(--bg3)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: Math.min(100, (count / (data.totalLeads || 1)) * 100) + "%", height: "100%", background: "linear-gradient(90deg,#534AB7,#1D9E75)", borderRadius: 4, transition: "width 0.6s ease" }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", minWidth: 28, textAlign: "right" }}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly trend chart */}
      {data && data.byMonth && (
        <div style={{ background: "var(--bg2)", borderRadius: 16, border: "0.5px solid rgba(83,74,183,0.3)", overflow: "hidden", boxShadow: "0 0 30px rgba(83,74,183,0.08)" }}>
          <div style={{ padding: "14px 20px", borderBottom: "0.5px solid var(--border)", background: "linear-gradient(135deg,rgba(83,74,183,0.08),rgba(29,158,117,0.06))", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#534AB7", boxShadow: "0 0 8px #534AB7" }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Leads &amp; appointments — last 6 months</span>
          </div>
          <div style={{ padding: "20px 16px 12px" }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.byMonth} barGap={4} margin={{ top: 4, right: 12, bottom: 0, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "var(--text3)", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: "var(--text3)", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "var(--bg2)", border: "0.5px solid var(--border)", borderRadius: 10, fontSize: 12, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}
                  labelStyle={{ color: "var(--text)", fontWeight: 700, marginBottom: 4 }}
                  cursor={{ fill: "rgba(83,74,183,0.06)" }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12, color: "var(--text2)" }} />
                <Bar dataKey="leads" name="Leads" fill="#534AB7" radius={[4, 4, 0, 0]} maxBarSize={32} />
                <Bar dataKey="appts" name="Appointments" fill="#1D9E75" radius={[4, 4, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!data && (
        <div style={{ textAlign: "center", padding: "40px", color: "var(--text3)", fontSize: 13 }}>Loading analytics...</div>
      )}
    </div>
  );
}